import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { showSuccess, showError } from "@/lib/toastHelpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Mail, Plus, Eye, Send, Trash2, Pencil, X } from "lucide-react";
import RichTextEditor from "@/components/RichTextEditor";

const TEMPLATE_TYPES = [
  "welcome", "purchase", "enrollment", "birthday", "referral",
  "password_reset", "cart_reminder", "cart_expiring", "cart_deleted", "custom",
];

const SAMPLE_VARS: Record<string, string> = {
  firstName: "Juan",
  email: "juan@example.com",
  OTP: "123456",
  businessName: "My Kitchen",
  roleName: "team member",
  inviteUrl: "https://chefaiq.com/join/abc123",
  workspaceName: "My Workspace",
  inviterName: "Grace",
  expiryDays: "7",
};

const applyVars = (html: string) =>
  Object.entries(SAMPLE_VARS).reduce(
    (s, [k, v]) => s.replace(new RegExp(`\\{\\{${k}\\}\\}`, "g"), v),
    html || ""
  );

export default function TemplatesTab() {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<any>(null);
  const [testModal, setTestModal] = useState<any>(null);
  const [testEmail, setTestEmail] = useState("");
  const [selected, setSelected] = useState<any>(null);
  const [form, setForm] = useState({ name: "", subject: "", htmlContent: "", type: "custom" });

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ["/api/email-mgmt/templates"],
    queryFn: () => apiRequest("GET", "/api/email-mgmt/templates") as unknown as Promise<any[]>,
  });

  const openCreate = () => {
    setSelected(null);
    setForm({ name: "", subject: "", htmlContent: "", type: "custom" });
    setDialogOpen(true);
  };

  const openEdit = (t: any) => {
    setSelected(t);
    setForm({ name: t.name, subject: t.subject, htmlContent: t.htmlContent, type: t.type });
    setDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (selected) {
        await apiRequest("PUT", `/api/email-mgmt/templates/${selected._id}`, form);
      } else {
        await apiRequest("POST", "/api/email-mgmt/templates", form);
      }
      showSuccess(selected ? "Template updated" : "Template created");
      setDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ["/api/email-mgmt/templates"] });
    } catch {
      showError("Failed to save template");
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this template?")) return;
    try {
      await apiRequest("DELETE", `/api/email-mgmt/templates/${id}`);
      showSuccess("Deleted");
      queryClient.invalidateQueries({ queryKey: ["/api/email-mgmt/templates"] });
    } catch {
      showError("Failed to delete");
    }
  };

  const [sendMode, setSendMode] = useState<'test'|'blast'>('test');
  const [selectedListId, setSelectedListId] = useState('');

  const { data: distLists = [] } = useQuery({
    queryKey: ['/api/email-mgmt/distribution'],
    queryFn: () => apiRequest('GET', '/api/email-mgmt/distribution') as unknown as Promise<any[]>,
    enabled: !!testModal,
  });

  const handleSendTest = async () => {
    try {
      await apiRequest("POST", `/api/email-mgmt/templates/${testModal._id}/test`, { testEmail });
      showSuccess(`Test sent to ${testEmail}`);
      setTestModal(null);
      setTestEmail("");
    } catch {
      showError("Failed to send test");
    }
  };

  const handleBlast = async () => {
    if (!selectedListId) return showError('Please select a distribution list');
    try {
      const res: any = await apiRequest('POST', `/api/email-mgmt/distribution/${selectedListId}/blast`, { templateId: testModal._id });
      showSuccess(`Sent to ${res.sent || 0} recipients`);
      setTestModal(null);
      setSelectedListId('');
    } catch {
      showError('Failed to send blast');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-muted-foreground text-sm">{templates.length} template{templates.length !== 1 ? "s" : ""}</p>
        <Button onClick={openCreate} size="sm" className="flex items-center gap-2">
          <Plus size={16} /> New Template
        </Button>
      </div>

      {isLoading ? (
        <p className="text-center text-gray-400 py-8">Loading...</p>
      ) : templates.length === 0 ? (
        <p className="text-center text-muted-foreground py-8">No templates yet</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {templates.map((t: any) => (
            <div key={t._id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2">
                  <Mail size={16} className="text-primary" />
                  <span className="font-semibold text-sm">{t.name}</span>
                </div>
                <span className="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 px-2 py-0.5 rounded-full">{t.type}</span>
              </div>
              <p className="text-xs text-muted-foreground mb-3 truncate">Subject: {t.subject}</p>
              <div className="flex gap-2">
                <Button onClick={() => openEdit(t)} size="sm" className="flex-1 text-xs h-7">
                  <Pencil size={12} className="mr-1" /> Edit
                </Button>
                <Button onClick={() => setPreviewTemplate(t)} size="sm" variant="outline" className="text-xs h-7 px-2">
                  <Eye size={14} />
                </Button>
                <Button onClick={() => { setTestModal(t); setTestEmail(""); }} size="sm" variant="outline" className="text-xs h-7 px-2 text-green-600 border-green-300">
                  <Send size={14} />
                </Button>
                <Button onClick={() => handleDelete(t._id)} size="sm" variant="destructive" className="text-xs h-7 px-2">
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white! text-gray-900!">
          <DialogHeader>
            <DialogTitle>{selected ? "Edit" : "Create"} Template</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 text-gray-900">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-700">Name *</Label>
                <Input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="mt-1 bg-white text-gray-900 border-gray-300" />
              </div>
              <div>
                <Label className="text-gray-700">Type</Label>
                <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}
                  className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white text-gray-900 focus:outline-hidden focus:ring-2 focus:ring-ring">
                  {TEMPLATE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label className="text-gray-700">Subject *</Label>
              <Input required value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} className="mt-1 bg-white text-gray-900 border-gray-300" />
            </div>
            <div>
              <Label>Body *</Label>
              <div className="mt-1">
                <RichTextEditor value={form.htmlContent} onChange={val => setForm(p => ({ ...p, htmlContent: val }))} />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2 border-t">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit">{selected ? "Update" : "Create"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Send Dialog */}
      <Dialog open={!!testModal} onOpenChange={() => { setTestModal(null); setSendMode('test'); setSelectedListId(''); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Send Email</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">Template: <strong>{testModal?.name}</strong></p>
          <div className="flex gap-2 border rounded-lg p-1">
            <button type="button" onClick={() => setSendMode('test')}
              className={`flex-1 text-xs py-1.5 rounded-md transition-colors ${sendMode === 'test' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>
              Test (single email)
            </button>
            <button type="button" onClick={() => setSendMode('blast')}
              className={`flex-1 text-xs py-1.5 rounded-md transition-colors ${sendMode === 'blast' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>
              Send to List
            </button>
          </div>
          {sendMode === 'test' ? (
            <Input type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="email1@example.com; email2@example.com" autoFocus />
          ) : (
            <div className="space-y-2">
              <select value={selectedListId} onChange={e => setSelectedListId(e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm bg-background focus:outline-hidden focus:ring-2 focus:ring-ring">
                <option value="">Select distribution list...</option>
                {(distLists as any[]).map((l: any) => (
                  <option key={l._id} value={l._id}>{l.name} ({l.memberCount ?? l.members?.length ?? 0} members)</option>
                ))}
              </select>
              {selectedListId && <p className="text-xs text-muted-foreground">Email will be sent to all members of this list.</p>}
            </div>
          )}
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => { setTestModal(null); setSendMode('test'); setSelectedListId(''); }}>Cancel</Button>
            <Button onClick={sendMode === 'test' ? handleSendTest : handleBlast} className="flex items-center gap-2">
              <Send size={16} /> {sendMode === 'test' ? 'Send Test' : 'Send to List'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewTemplate} onOpenChange={() => setPreviewTemplate(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{previewTemplate?.name}</DialogTitle>
            <p className="text-sm text-muted-foreground">Subject: {applyVars(previewTemplate?.subject || "")}</p>
          </DialogHeader>
          <iframe
            srcDoc={applyVars(previewTemplate?.htmlContent || "")}
            className="flex-1 min-h-[500px] border-0 rounded"
            title="Preview"
            sandbox=""
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
